import React, { CSSProperties, memo } from 'react';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Loading } from '../Loading';

export interface SwitchProps {
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  inputId?: string;
  'data-test'?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  style?: CSSProperties;
  loading?: boolean;
  readOnly?: boolean;
}

export const Switch: React.FC<React.PropsWithChildren<SwitchProps>> = memo((props) => (
  <StyledSwitch>
    {
      // eslint-disable-next-line styled-components-a11y/label-has-associated-control
      <StyledContainer className={props.className} data-test={props['data-test']}>
        <StyledInput
          disabled={props.disabled}
          type="checkbox"
          id={props.id}
          checked={!!props.checked}
          onChange={props.onChange}
          name={props.name}
          style={props.style}
          readOnly={props.readOnly}
        />
        <StyledSpan
          className={`checkmark ${props.checked ? 'checked' : ''} ${props.disabled ? 'disabled' : ''}`}
          $checked={!!props.checked}
          disabled={!!props.disabled}
          role="switch"
          aria-checked={!!props.checked}
          aria-readonly={!!props.disabled}
        />
      </StyledContainer>
    }
    {props.loading && (
      <StyledLoading>
        <Loading size="small" margin="none" />
      </StyledLoading>
    )}
  </StyledSwitch>
));

const StyledLoading = styled.div`
  margin-left: 0.5rem;
`;

export default Switch;

const StyledSwitch = styled.div`
  display: inline-flex;
`;

const StyledContainer = styled.label`
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
`;

const StyledInput = styled.input`
  opacity: 0;
  visibility: hidden;
  width: 0;
  height: 0;
`;

const StyledSpan = styled.span<{ $checked: boolean; disabled: boolean }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: 0.2s;
  border: ${({ $checked }) => ($checked ? `1px solid ${tokens.colorPrimary}` : '1px solid #5E6A75')};
  box-sizing: border-box;
  border-radius: 10px;
  background-color: ${({ $checked }) => ($checked ? tokens.colorPrimary : tokens.colorSurface)};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)} !important;
  cursor: ${({ disabled }) => (disabled ? 'unset' : 'pointer')};

  &::before {
    position: absolute;
    z-index: 100;
    border-radius: 50%;
    content: '';
    height: 14px;
    width: 14px;
    left: 1px;
    top: 1px;
    background-color: ${({ $checked: checked }) => (checked ? tokens.colorSurface : tokens.colorOnSurfaceVariant)};
    transition: 0.2s;
    transform: ${({ $checked: checked }) => (checked ? 'translateX(16px)' : 'translateX(0)')};
    box-sizing: border-box;
  }

  &::after {
    z-index: -1;
    position: absolute;
    border-radius: 50%;
    content: '';
    height: 28px;
    width: 28px;
    left: -6px;
    bottom: -6px;
    background-color: ${({ $checked: checked }) =>
      checked ? tokens.colorPrimaryContainer : tokens.paletteNeutralVariant95};
    transition: transform 0.2s;
    transform: ${({ $checked: checked }) => (checked ? 'translateX(16px)' : 'translateX(0)')};
    transition: transform 0.2s;
    box-sizing: border-box;
    visibility: hidden;
  }

  &:not(.checked):not(.disabled):hover:after {
    visibility: visible;
    z-index: 100;
    mix-blend-mode: multiply;
  }

  &.checked:not(.disabled):hover:after {
    visibility: visible;
    z-index: -1;
    opacity: 0.3;
  }

  &:not(.checked):not(.disabled):active:after {
    visibility: visible;
    z-index: 100;
    background-color: ${tokens.colorSurfaceVariant};
    transition: 0.2s;
    mix-blend-mode: multiply;
  }

  &.checked:not(.disabled):active:after {
    visibility: visible;
    z-index: -1;
    opacity: 1;
  }
`;
// https://www.w3schools.com/howto/tryit.asp?filename=tryhow_css_switch
